<template>
  <v-container fluid>
    <PageHeader
      title="Photos"
      icon="mdi-camera"
    >
      <template #actions>
        <v-btn
          v-if="hasScope('images:write')"
          color="primary"
          @click="openUpload"
        >
          Upload New Photo
        </v-btn>
      </template>
    </PageHeader>
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col class="pr-6">
            <v-menu
              v-model="dateField"
              :close-on-content-click="false"
              :offset="40"
              transition="scale-transition"
              min-width="auto"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="dateRangeText"
                  label="Date Filter"
                  prepend-icon="mdi-calendar"
                  readonly
                  density="compact"
                  v-bind="props"
                  clearable
                  v-on="props"
                  @click:clear="dateTimeFullText = null"
                />
              </template>
              <v-date-picker
                v-model="dateTimeFullText"
                multiple="range"
                show-adjacent-months
              />
            </v-menu>
          </v-col>
          <v-col>
            <v-text-field
              v-model="query"
              prepend-icon="mdi-magnify"
              label="Search"
              density="compact"
              single-line
              hide-details
              clearable
            />
          </v-col>
          <v-col>
            <v-select
              v-model="selectedSpecies"
              :items="Object.values(enums.sightingSpecies)"
              label="Species"
              name="species"
              density="compact"
              multiple
              clearable
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn-toggle
              v-model="association"
              mandatory
              density="compact"
              color="primary"
              variant="outlined"
              divided
            >
              <v-btn value="all">
                All
              </v-btn>
              <v-btn value="assigned">
                Assigned
              </v-btn>
              <v-btn value="unassigned">
                Unassigned
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-tabs
          v-model="tab"
          fixed-tabs
        >
          <v-tab
            value="gallery"
            text="Gallery"
          />
          <v-tab
            value="table"
            text="Table"
          />
        </v-tabs>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-tabs-window v-model="tab">
          <v-tabs-window-item
            value="gallery"
          >
            <v-row>
              <v-col
                v-for="item in items"
                :key="item.id"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card variant="outlined">
                  <v-img
                    :src="thumbnailUrl(item)"
                    aspect-ratio="1"
                    class="bg-grey-lighten-2 clickable"
                    cover
                    @click="enlarge(item)"
                  >
                    <template #placeholder>
                      <v-row
                        align="center"
                        class="fill-height ma-0"
                        justify="center"
                      >
                        <v-progress-circular
                          color="grey-lighten-5"
                          indeterminate
                        />
                      </v-row>
                    </template>
                  </v-img>
                  <v-card-subtitle class="py-2">
                    {{ item.name }}
                  </v-card-subtitle>
                </v-card>
              </v-col>
            </v-row>
          </v-tabs-window-item>
          <v-tabs-window-item
            value="table"
          >
            <v-row>
              <v-col>
                <v-sheet
                  elevation="2"
                  class="pa-md-2"
                >
                  <v-data-table-server
                    v-model:options="options"
                    :headers="headers"
                    :items="items"
                    :loading="loading"
                    :items-length="total"
                  >
                    <template #[`item.thumbnail`]="{ item }">
                      <v-img
                        :src="thumbnailUrl(item)"
                        width="56"
                        height="56"
                        cover
                        class="clickable my-1"
                        @click="enlarge(item)"
                      />
                    </template>
                    <template #[`item.imageNumber`]="{ item }">
                      {{ disambiguatedImageNumber(item) }}
                    </template>
                    <template #[`item.actions`]="{ item }">
                      <div class="d-flex align-center justify-center">
                        <v-tooltip location="bottom">
                          <template #activator="{ props }">
                            <span v-bind="props">
                              <v-btn
                                icon
                                variant="text"
                                size="small"
                                class="mr-1"
                                :disabled="!item.sightingNumber || item.sightingNumber === 'na'"
                                @click="viewSighting(item)"
                              >
                                <v-icon size="small">mdi-eye</v-icon>
                              </v-btn>
                            </span>
                          </template>
                          <span>View Sighting</span>
                        </v-tooltip>
                        <v-tooltip location="bottom">
                          <template #activator="{ props }">
                            <span v-bind="props">
                              <v-btn
                                icon
                                variant="text"
                                size="small"
                                class="mr-1"
                                :disabled="!item.whaleId || item.whaleId === 'na'"
                                @click="viewWhale(item)"
                              >
                                <v-icon size="small">mdi-whale</v-icon>
                              </v-btn>
                            </span>
                          </template>
                          <span>View Whale</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </v-data-table-server>
                </v-sheet>
              </v-col>
            </v-row>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-col>
    </v-row>
    <photo-upload />
    <sighting-details :view-only="true" />
    <whale-quick-view />
    <v-dialog
      v-model="enlargeDialog"
      max-width="900px"
    >
      <v-card>
        <v-img :src="enlargedUrl" />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PageHeader from "@/components/PageHeader";
import { debounce } from "lodash";
import PhotoUpload from "@/components/PhotoUpload.vue";
import SightingDetails from "@/components/sighting/Details.vue";
import WhaleQuickView from "@/components/whale/WhaleQuickView.vue";
import enums from "@/helpers/enums";
import { disambiguatedImageNumber } from "@/helpers/imageDisplay";

export default {
  components: {
    PageHeader,
    PhotoUpload,
    SightingDetails,
    WhaleQuickView,
  },
  data() {
    return {
      query: "",
      options: {},
      tab: "gallery",
      dateTimeFullText: null,
      dateField: false,
      selectedSpecies: [],
      association: "all",
      enlargeDialog: false,
      enlargedUrl: "",
      headers: [
        { title: "", sortable: false, key: "thumbnail", width: "72px" },
        { title: "Name", sortable: true, key: "name" },
        { title: "Image Number", sortable: true, key: "imageNumber" },
        { title: "Whale ID", sortable: true, key: "whaleId" },
        { title: "Sighting Number", sortable: true, key: "sightingNumber" },
        { title: "Uploaded By", sortable: true, key: "uploadedBy" },
        {
          title: "Actions",
          sortable: false,
          key: "actions",
          align: "center",
          width: "10%",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      loading: "imageSearch/loading",
      items: "imageSearch/items",
      searchOptions: "imageSearch/searchOptions",
      searchQuery: "imageSearch/searchQuery",
      searchDate: "imageSearch/searchDate",
      searchSpecies: "imageSearch/searchSpecies",
      searchAssociation: "imageSearch/searchAssociation",
      total: "imageSearch/total",
      hasScope: "auth/hasScope",
    }),
    startDatetime () {
      if(this.dateTimeFullText) {
        if(this.dateTimeFullText.length > 1) {
          return this.dateTimeFullText[0].toString();
        }
        else {
          return this.dateTimeFullText?.toString();
        }
      }
      else {
        return "";
      }
    },
    startYear () {
      return this.startDatetime?.split(" ")[3];
    },
    startMonth () {
     const numericalMonth = this.enums.shortMonths.indexOf(this.startDatetime?.split(" ")[1]) + 1;
     if (numericalMonth < 10) {
       return `0${numericalMonth}`;
     }
     else {
       return numericalMonth;
     }
    },
    startDay () {
      return this.startDatetime?.split(" ")[2];
    },
    startDate () {
      return `${this.startYear}-${this.startMonth}-${this.startDay}`;
    },
    endDatetime () {
      if(this.dateTimeFullText) {
        if(this.dateTimeFullText.length > 1) {
          return this.dateTimeFullText[this.dateTimeFullText.length-1].toString();
        }
        else {
          return "";
        }
      }
      else {
        return "";
      }
    },
    endYear () {
      return this.endDatetime?.split(" ")[3];
    },
    endMonth () {
      const numericalMonth = this.enums.shortMonths.indexOf(this.endDatetime?.split(" ")[1]) + 1;
      if (numericalMonth < 10) {
        return `0${numericalMonth}`;
      }
      else {
        return numericalMonth;
      }
    },
    endDay () {
      return this.endDatetime?.split(" ")[2];
    },
    endDate () {
      return `${this.endYear}-${this.endMonth}-${this.endDay}`;
    },
    dateRangeText () {
      if (this.startYear !== "" && this.startYear !== undefined) {
        if (this.endYear !== "" && this.endYear !== undefined) {
          return `${this.startDate}~${this.endDate}`;
        }
        else {
          return this.startDate;
        }
      }
      else {
        return "";
      }
    },
    enums() {
      return enums;
    },
  },
  watch: {
    query() {
      if (this.query == null) this.query = "";
      this.debouncedSearch();
      this.options.page = 1;
    },
    dateTimeFullText() {
      this.debouncedSearch();
      this.options.page = 1;
    },
    async selectedSpecies() {
      await this.executeSearch()
    },
    async association() {
      await this.executeSearch()
    },
    options() {
      this.executeSearch();
    },
  },
  async mounted() {
    await this.reload();
  },
  created() {
    this.options = this.searchOptions;
    this.query = this.searchQuery;
    if (this.searchSpecies != null) {
      this.selectedSpecies = this.searchSpecies;
    }
    if (this.searchAssociation != null) {
      this.association = this.searchAssociation;
    }
    this.debouncedSearch = debounce(this.executeSearch, 250);
  },
  methods: {
    ...mapActions({
      search: "imageSearch/search",
      reload: "imageSearch/reload",
      openUpload: "images/open",
      openSightingByNumber: "sighting/details/openByNumber",
      openWhaleByWhaleId: "whale/view/openByWhaleId",
    }),
    async executeSearch() {
      await this.search({
        ...this.options,
        query: this.query,
        date: this.dateRangeText?.split("~"),
        species: this.selectedSpecies,
        association: this.association,
      });
    },
    disambiguatedImageNumber,
    resolveStorageUrl(url) {
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    thumbnailUrl(item) {
      return this.resolveStorageUrl(item.thumbnailUrl);
    },
    fullImageUrl(item) {
      return this.resolveStorageUrl(item.imageUrl);
    },
    enlarge(item) {
      this.enlargedUrl = this.fullImageUrl(item);
      this.enlargeDialog = true;
    },
    viewSighting(item) {
      this.openSightingByNumber(item.sightingNumber);
    },
    viewWhale(item) {
      this.openWhaleByWhaleId({ whaleId: item.whaleId, mode: "quick" });
    },
  },
};
</script>

<style scoped>


.center-vertically {
  align-items: center;
  display: flex;
}

.clickable {
  cursor: pointer;
}

</style>
